import { useMemo, ReactNode } from "react";

// react-chartjs-2 components
import { Line } from "react-chartjs-2";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// Material Dashboard 2 PRO React TS components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// DefaultLineChart configurations
import configs from "examples/Charts/LineCharts/DefaultLineChart/configs";
import { yAxis } from "examples/Charts/LineCharts/DefaultLineChart/configs";

// Custom components
import CardSkeleton from "examples/Cards/CardSkeleton";
import CardError from "examples/Cards/CardError";

// Material Dashboard 2 PRO React TS Base Styles
import colors from "assets/theme/base/colors";

// Declaring props types for DefaultLineChart
interface Props {
  icon?: {
    color?: "primary" | "secondary" | "info" | "success" | "warning" | "error" | "light" | "dark";
    component: ReactNode;
  };
  title?: string;
  description?: string | ReactNode;
  height?: string | number;
  chart: {
    labels: string[];
    datasets: {
      label: string;
      color: "primary" | "secondary" | "info" | "success" | "warning" | "error" | "light" | "dark";
      data: number[];
    }[];
  };
  stacked?: boolean;
  pointed?: boolean;
  xAxis?: string;
  yAxis?: yAxis;
  xMinMax?: { min: number; max: number; ticks?: number[] };
  tooltipPrecision?: number;
  legendPosition?: "top" | "left" | "bottom" | "right" | "chartArea";
  isLoading?: boolean;
  isError?: boolean;
  errorMessage?: string;
  onRetry?: () => void;
  [key: string]: any;
}

function DefaultLineChart({
  icon = { color: "info", component: "" },
  title = "",
  description = "",
  height = "20rem",
  chart,
  stacked,
  pointed,
  xAxis,
  yAxis,
  xMinMax,
  tooltipPrecision,
  legendPosition,
  isLoading = false,
  isError = false,
  errorMessage,
  onRetry,
}: Props): JSX.Element {
  const chartDatasets = chart.datasets
    ? chart.datasets.map((dataset) => ({
        ...dataset,
        tension: 0,
        pointRadius: pointed ? 3 : 0,
        borderWidth: 3,
        backgroundColor:
          colors[dataset.color] && !pointed && stacked
            ? colors[dataset.color || "dark"].main
            : "transparent",
        fill: !stacked
          ? true
          : {
              target: "origin",
              above: colors[dataset.color]
                ? colors[dataset.color || "dark"].main
                : colors.dark.main,
              below: colors[dataset.color]
                ? colors[dataset.color || "dark"].main
                : colors.dark.main,
            },
        pointBackgroundColor: colors[dataset.color]
          ? colors[dataset.color || "dark"].main
          : colors.dark.main,
        borderColor:
          colors[dataset.color] && (pointed || !stacked)
            ? colors[dataset.color || "dark"].main
            : colors.dark.main,
        maxBarThickness: 6,
      }))
    : [];

  const { data, options } = configs(
    chart.labels || [],
    chartDatasets,
    stacked,
    xAxis,
    yAxis,
    xMinMax,
    tooltipPrecision,
    legendPosition
  );

  const chartContent = useMemo(
    () => (
      <MDBox height={height}>
        <Line data={data} options={options} />
      </MDBox>
    ),
    [chart, height]
  );

  const renderChart = (
    <MDBox py={0.5}>
      <Accordion defaultExpanded sx={{ boxShadow: "none" }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          {title || description ? (
            <MDBox display="flex" px={description ? 1 : 0} pt={description ? 1 : 0} mt={-2}>
              {icon?.component && (
                <MDBox
                  width="3rem"
                  height="3rem"
                  bgColor={icon.color || "info"}
                  variant="gradient"
                  coloredShadow={icon.color || "info"}
                  borderRadius="xl"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  color="white"
                  mr={2}
                >
                  <Icon fontSize="medium">{icon?.component}</Icon>
                </MDBox>
              )}
              <MDBox>
                {title && <MDTypography variant="h6">{title}</MDTypography>}
                <MDTypography component="div" variant="button" color="text">
                  {description}
                </MDTypography>
              </MDBox>
            </MDBox>
          ) : null}
        </AccordionSummary>
        <AccordionDetails>{chartContent}</AccordionDetails>
      </Accordion>
    </MDBox>
  );

  if (isLoading) {
    return <CardSkeleton height={height} />;
  }

  if (isError) {
    return <CardError height={height} message={errorMessage} onRetry={onRetry} />;
  }

  return title || description ? <Card>{renderChart}</Card> : renderChart;
}

// Declaring default props DefaultLineChart
DefaultLineChart.defaultProps = {
  icon: { color: "info", component: "" },
  title: "",
  description: "",
  height: "20rem",
  legendPosition: "chartArea",
  isLoading: false,
  isError: false,
};

export default DefaultLineChart;
